import { Alert, Autocomplete, Box, Button, Container, DialogTitle, Stack, Switch, TextField, Tooltip } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useMemo, useEffect, useCallback } from "react";
import { useApi } from "../../../utils/Api";
import Preloader from "../../Preloader";
import EPortAvatar from "../../EPortAvatar";
import Translate from "../../../utils/Translate";
import * as Yup from "yup";
import debounce from "lodash/debounce";
import AutocompleteSearchField from "../../AutocompleteSearchField";
import ActorAutocompleteOption from "../../ActorAutocompleteOption";
import { actorSearch, actorPersonTypes } from "../../../utils/actorSearch";
import { tryCreatePersonnummerWithSettings, getPersonnummerPlaceholderText } from "../../../utils/personnummer";

import { validatePhoneNumber } from "../../../utils/phoneNumber";

// Get the text for submit button when saving a new actor
function getAddActorSubmitText(actorRole) {
  switch (actorRole) {
    case "teacher":
      return Translate.get("AddInstructor");
    case "student":
      return Translate.get("AddStudent");
    case "contact":
      return Translate.get("AddContact");
    case "permit-holder":
      return Translate.get("AddPerson2"); // Lägg till innehavare
    default:
      return Translate.get("AddPerson");
  }
}

// Get the text for submit button when saving an existing actor
function getUpdateActorSubmitText(actorRole, isEditOnly, hasSwitchedActor, actorId) {
  // isEditOnly determines whether we're only editing an actor or also selecting an actor for a purpose.
  // For example, consider the difference between simply editing an instructor via the list in settings,
  // and editing an instructor via a specific course. In the latter case we want a change of actor in
  // this dialog to change the instructor for the course too, and make that effect clear to the user.
  if (isEditOnly || (actorId > 0 && !hasSwitchedActor)) {
    return Translate.get("Save");
  } else {
    switch (actorRole) {
      case "teacher":
        return Translate.get("SelectInstructor");
      case "student":
        return Translate.get("SelectStudent");
      case "contact":
        return Translate.get("SelectContact");
      case "permit-holder":
        return Translate.get("SelectHolder"); // Välj innehavare
      default:
        return Translate.get("SelectPerson");
    }
  }
}

const PersonElectronicContactsCrud = ({
  dataId,
  actorId,
  parentActorId,
  name,
  actorRole,
  isEditOnly,
  apiFetchActor,
  apiSaveActorElectronicContacts,
  onActorSaved,
  onClose,
  onCancel,
  hideHeader,
  parentZIndexOverride,
}) => {
  if (
    (actorRole === "student" || actorRole === "contact" || actorRole == "permit-holder") &&
    (!parentActorId || parentActorId <= 0)
  ) {
    throw new Error("Missing parentActorId");
  }

  const [originalEmail, setOriginalEmail] = useState();
  const [hasEmailChanged, setHasEmailChanged] = useState(false);
  const [originalPersonalNo, setOriginalPersonalNo] = useState();
  const [hasPersonalNoChanged, setHasPersonalNoChanged] = useState(false);
  const [submitText, setSubmitText] = useState("OK");
  const [headerText, setHeaderText] = useState(Translate.get("EditPersonalInfo"));
  const [formData, setFormData] = useState({});
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [errorMess, setErrorMess] = useState({});
  const [isBlankForm, setIsBlankForm] = useState(true);
  const [dataFetched, setDataFetched] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const [personnummerPlaceholderText, setPersonnummerPlaceholderText] = useState("");
  const [invalidOrgNoMessage, setInvalidOrgNoMessage] = useState("");
  const [oldInvalidPersonalNumber, setOldInvalidPersonalNumber] = useState(null);


  const api = useApi();
  const location = useLocation();
  const navigate = useNavigate();

  const schema = Yup.object({
    required: Yup.string().min(1, Translate.get("ePortNameRequired")).optional(),
    email: Yup.string().email(Translate.get("InvalidEmail")).optional(),
    emailRequired: Yup.string().email(Translate.get("InvalidEmail")).optional(),

    mobilePhone: Yup.string()
      .test("mobilePhone", Translate.get("InvalidPhoneNumber"), function (item) {
        if (!item) {
          return true;
        }

        let validationResult = validatePhoneNumber(item);
        return validationResult;
      })
      .optional(),
  });

  useEffect(() => {
    getPersonnummerPlaceholderText().then((result) => setPersonnummerPlaceholderText(result));
  }, []);

  const closeAndReload = () => {
    //Soft reload with redirect
    const hash = window.location.hash.endsWith("#dialog")
      ? window.location.hash.substring(0, window.location.hash.length - 7)
      : window.location.hash;
    const loc = encodeURIComponent(`${location.pathname}${location.search}${hash}`);
    navigate(`/redirect/${loc}`, { replace: true });
  };


  const actorNameAutocompleteSearch = useCallback(async (searchText) => {
    var foundActors = await actorSearch(searchText, actorPersonTypes, ["ActorName"], 50);
    if (foundActors) {
      return foundActors.map((actor) => ({
        actorId: actor.actorId,
        actorName: actor.actorName,
        orgNo: actor.orgNo,
        email: actor.email,
        companies: actor.parentActors
          ?.filter(
            (x, index, allParentActors) =>
              x.parentActor.actorId > 0 &&
              x.parentActor.actorId !== actor.actorId &&
              allParentActors.findIndex((y) => y.parentActor.actorId === x.parentActor.actorId) === index
          )
          .map((x) => x.parentActor.actorName),
      }));
    }
  }, []);

  const loadActor = async (id) => {
    //await api.fetch(`${import.meta.env.VITE_MAIN_URL}actors/${id}/as-person`, false, "GET");
    const data = await apiFetchActor(id);
    if (data) {
      const existingActorFormData = {
        actorId: data.actorId,
        actorName: data.actorName,
        externalActorNo: data.externalActorNo,
        // For some reason null BirthDates are set to empty
        // string, so converting back to null in that case
        birthDate: data.birthDate || null,
        orgNo: data.orgNo,
        actorEmail: data.actorEmail,
        isInactive: data.isInactive,
        isConnectedToUser: data.isConnectedToUser,
        isConnectedToUserAtClient: data.isConnectedToUserAtClient,
        isConnectedToUserAtIssuer: data.isConnectedToUserAtIssuer,
        profilePictureUrl: data.profilePictureUrl,
        observationNote: data.observationNote,
        defaultCustomerOrderNo: data.defaultCustomerOrderNo,
        hasApprovedGDPR: data.hasApprovedGDPR,
        gdprApprovalDate: data.gdprApprovalDate,
        mobilePhone: {
          actorId: data.actorId,
          actorPhoneNumber: data.mobilePhone ? data.mobilePhone.actorPhoneNumber : null,
        },
        postalAddress: {
          actorId: data.actorId,
          actorAddressId: data.postalAddress?.actorAddressId,
          actorAddressStreet2: data.postalAddress?.actorAddressStreet2,
          actorAddressCity: data.postalAddress?.actorAddressCity,
          actorAddressZipCode: data.postalAddress?.actorAddressZipCode,
          actorAddressCountry: data.postalAddress?.actorAddressCountry,
        },
        nationality: data.nationality,
      };
      setOriginalEmail(data.actorEmail);
      setHasEmailChanged(false);
      setOriginalPersonalNo(data.orgNo);
      setHasPersonalNoChanged(false);
      setIsBlankForm(false);
      setFormData(existingActorFormData);
      setDataFetched(true);
    }
  };


  const submitFormData = async (formData) => {
    const actorElectronicContactFormData = {
      dataId,
      actorId: formData.actorId,
      actorEmail: formData.actorEmail,
      mobilePhone: formData.mobilePhone.actorPhoneNumber
    };

    setIsSubmitLoading(true);

    const data = await apiSaveActorElectronicContacts(actorElectronicContactFormData);

    //const data = await api.fetch(`${import.meta.env.VITE_MAIN_URL}permits/responsible-view/holder/electronic-contacts`, actorElectronicContactFormData);

    if (data && data.isSuccessful !== false) {
      if (onActorSaved) {
        await onActorSaved(data);
      }

      if (onClose) {
        onClose();
      } else {
        closeAndReload();
      }
    }
    setIsSubmitLoading(false);
  };

  function validateInput(input, value, validator) {
    value = value ?? "";
    let result;
    try {
      result = schema.validateSync({ [validator]: value }) && false;
    } catch (err) {
      if (err.errors) {
        result = err.errors[0];
      }
    }
    setErrorMess((oldValue) => ({
      ...oldValue,
      [input]: result,
    }));
  }

  useEffect(() => {
    if (isBlankForm) {
      setSubmitDisabled(true);
    }
  }, [isBlankForm]);

  useEffect(() => setSubmitDisabled(Object.values(errorMess).some((x) => x)), [errorMess]);

  useEffect(() => {
    if (actorId) {
      loadActor(actorId);
      setHeaderText(Translate.get("EditContactDetails"));
      setSubmitText(getUpdateActorSubmitText(actorRole, isEditOnly, false, actorId));
    } else {
      const addActorSubmitText = getAddActorSubmitText(actorRole);
      setHeaderText(addActorSubmitText);
      setSubmitText(addActorSubmitText);
      const blankFormData = {
        actorName: name ?? null,
        externalActorNo: null,
        birthDate: null,
        orgNo: null,
        actorEmail: null,
        mobilePhone: { actorPhoneNumber: null },
        postalAddress: {
          actorAddressStreet2: null,
          actorAddressCity: null,
          actorAddressZipCode: null,
          actorAddressCountry: null,
        },
      };

      setOriginalEmail(null);
      setHasEmailChanged(false);
      setOriginalPersonalNo(null);
      setHasPersonalNoChanged(false);
      setIsBlankForm(!name);
      setFormData(blankFormData);
      setDataFetched(true);
    }
  }, [actorId, name, actorRole, isEditOnly]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if(formData.actorEmail?.length === 0 && formData.mobilePhone.actorPhoneNumber?.length === 0) {
      setErrorMess({
        ...errorMess,
        noEmailOrMobile: Translate.get("InvalidOrMissingEmailAndPhone")
      });
      return false;
    } 
    
    submitFormData(formData);
  };

  function handleActorNameAutocompleteChange(actor) {
    if (actor && actor.actorId > 0) {
      setDataFetched(false);
      loadActor(actor.actorId);
      setHeaderText(Translate.get("EditPersonalInfo"));
      setSubmitText(getUpdateActorSubmitText(actorRole, isEditOnly, true, actorId));
    } else {
      setFormData({
        ...formData,
        actorName: actor?.actorName,
      });
      validateInput("actorName", actor?.actorName, "required");
      setIsBlankForm(false);
    }
  }

  function handleCancel() {
    if (onCancel) {
      onCancel();
    }

    if (onClose) {
      onClose();
    } else {
      closeAndReload();
    }
  }

  // Using this to avoid validating the potentially empty
  // name value when opening an empty form the first time
  const isActorNameBlank = !("actorName" in formData) || typeof formData?.actorName === typeof null;

  return (
    <>
      {!dataFetched && (
        <>
          <Preloader />
          {!hideHeader && <DialogTitle>{headerText}</DialogTitle>}
          <DialogContent className="stdDialogContent"></DialogContent>
        </>
      )}
      {dataFetched && (
        <>
          {!hideHeader && <DialogTitle>{headerText}</DialogTitle>}
          <DialogContent dividers={true} className="stdDialogContent dialogOverlay">
            {/* <FormControl fullWidth margin="normal" size='medium' fullWidth={true} onSubmit={handleSubmit}> */}
            <form onSubmit={handleSubmit} className="twoColForm">
              <div className="formAvatar">
                <div className="smallHeader">{Translate.get("ProfilePicture")}</div>
                <EPortAvatar
                  avatarWidth={160}
                  avatarHeight={160}
                  fontSize={72}
                  displayName={formData.actorName ? formData.actorName : "?"}
                  pictureUrl={formData.profilePictureUrl + ""}
                  showDeleteButton={false}
                  showUploadButton={false}
                  //disableSubmit={setSubmitDisabled}
                  imageType={"person"}
                />
              </div>
              <div className="stdForm">
                <div className="smallHeader">{Translate.get("PersonalInfo")}</div>
                <div>
                  <AutocompleteSearchField
                    disabled={true}
                    placeholder={Translate.get("NameFieldHelptext")}
                    label={Translate.get("Name")}
                    value={{ actorName: formData.actorName ?? "" }}
                    onValueChange={handleActorNameAutocompleteChange}
                    getOptionLabel={(option) => option?.actorName ?? ""}
                    renderOption={(option) => (
                      <ActorAutocompleteOption
                        actorName={option?.actorName}
                        orgNo={option?.orgNo}
                        email={option?.email}
                        companies={option?.companies}
                      />
                    )}
                    keyPropName="actorId"
                    search={
                      !formData || !formData.actorId || formData.actorId <= 0 ? actorNameAutocompleteSearch : null
                    }
                    createNewValue={(text) => ({
                      actorName: text,
                    })}
                    textFieldProps={{
                      required: true,
                      error: !isActorNameBlank && !!errorMess["actorName"],
                      helperText:
                        (invalidOrgNoMessage.length > 0 ? " " : "") || (!isActorNameBlank && errorMess["actorName"]),
                    }}
                    hideOptionsFilter={formData.actorId > 0 && ((option) => option.actorId === formData.actorId)}
                    disablePortal={parentZIndexOverride}
                  />
                  <TextField
                    disabled={true}
                    inputProps={{ maxLength: 13 }}
                    fullWidth
                    value={formData.orgNo ?? ""}
                    onChange={(event, value) => {
                      setFormData({ ...formData, orgNo: event.target.value });
                      setIsBlankForm(false);
                      setHasPersonalNoChanged(originalPersonalNo !== event.target.value);
                    }}
                    label={Translate.get("PersonalNo")}
                    placeholder={Translate.get(personnummerPlaceholderText)}
                    error={!!invalidOrgNoMessage}
                    helperText={invalidOrgNoMessage || (!isActorNameBlank && errorMess["actorName"] && " ")}
                  />
                </div>
                <div className="smallHeader">{Translate.get("ContactInformation")}</div>
                <div>
                  <TextField
                    fullWidth
                    spellCheck={false}
                    value={formData.actorEmail ?? ""}
                    onChange={(event, value) => {
                      setFormData({
                        ...formData,
                        actorEmail: event.target.value,
                      });
                      if (errorMess.actorEmail) {
                        validateInput("actorEmail", event.target.value, "email");
                      }
                      if(event.target.value) {
                       // tabort ev felmeddelande att både mail och mobil saknas
                        const {noEmailOrMobile, ...keep} = errorMess;
                        setErrorMess(keep);
                      }

                      setIsBlankForm(false);
                      setHasEmailChanged(originalEmail !== event.target.value);
                    }}
                    onBlur={() => validateInput("actorEmail", formData?.actorEmail, "email")}
                    label={Translate.get("Email")}
                    error={!!errorMess["actorEmail"]}
                    helperText={errorMess["actorEmail"] ? errorMess["actorEmail"] : " "}
                  />
                  <TextField
                    fullWidth
                    value={formData.mobilePhone.actorPhoneNumber ?? ""}
                    onChange={(event, value) => {
                      setFormData({
                        ...formData,
                        mobilePhone: {
                          actorPhoneNumber: event.target.value,
                          actorId: formData.actorId,
                        },
                      });
                      setIsBlankForm(false);
                      if (errorMess.actorPhoneNumber) {
                        validateInput("actorPhoneNumber", event.target.value, "mobilePhone");
                      }

                      if(event.target.value) {
                        // tabort ev felmeddelande att både mail och mobil saknas
                        const {noEmailOrMobile, ...keep} = errorMess;
                        setErrorMess(keep);
                      }
                    }}
                    onBlur={() =>
                      validateInput("actorPhoneNumber", formData?.mobilePhone?.actorPhoneNumber, "mobilePhone")
                    }
                    label={Translate.get("MobilePhone")}
                    error={!!errorMess["actorPhoneNumber"]}
                    helperText={errorMess["actorPhoneNumber"] ? errorMess["actorPhoneNumber"] : " "}
                  />
                </div>
              
                {errorMess.noEmailOrMobile 
                  && <Alert severity="error">{errorMess.noEmailOrMobile}</Alert>
                }
              </div>

            </form>
            {/* </FormControl> */}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel}>{Translate.get("Cancel")}</Button>
            <Button
              className="stdLightLoader"
              loading={isSubmitLoading}
              disabled={submitDisabled}
              onClick={handleSubmit}
            >
              {submitText}
            </Button>
          </DialogActions>
          {/* Allt innehåll */}
        </>
      )}
    </>
  );
};
export default PersonElectronicContactsCrud;
